import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Maximize2, RefreshCw, Volume2, Wallet, User } from "lucide-react";
import { GAME_URL } from "../data/mock";
import { useWallet } from "../context/WalletContext";
import { recordSession, sendHeartbeat } from "../lib/api";
import ContractAddress from "../components/ContractAddress";

const HEARTBEAT_SECONDS = 15;

const Play = () => {
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);
  const recordedRef = useRef(false);
  const sessionIdRef = useRef(null);
  const { address, shortAddress, isConnected } = useWallet();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isGuest = params.get("guest") === "1";

  useEffect(() => {
    document.title = "PokéRealms · Play";

    // If not guest and not connected, send user back to home to connect first
    if (!isGuest && !isConnected) {
      navigate("/", { replace: true });
      return;
    }

    // Record session once per page-load, then start tracking active play time
    if (!recordedRef.current) {
      recordedRef.current = true;
      recordSession(isGuest ? null : address).then((id) => {
        sessionIdRef.current = id;
      });
    }
  }, [isGuest, isConnected, address, navigate]);

  // Heartbeat: count active play time while the tab is visible
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible" && sessionIdRef.current) {
        sendHeartbeat(sessionIdRef.current, HEARTBEAT_SECONDS);
      }
    }, HEARTBEAT_SECONDS * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  const handleReload = () => {
    setLoaded(false);
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen bg-[#0b1626] flex flex-col">
      {/* Top bar */}
      <div className="h-14 px-4 sm:px-6 flex items-center justify-between border-b border-white/10 bg-[#070f1c]">
        <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-[#ffcb05] transition-colors text-sm">
          <ArrowLeft size={18} />
          <span className="pixel-font text-[10px] tracking-wider">BACK</span>
        </Link>

        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 rounded-full relative overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg,#ee1515 0 49%, #1a1a1a 49% 53%, #f5f5f5 53% 100%)",
            }}
          >
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border border-[#1a1a1a]" />
          </div>
          <span className="pixel-font text-[#ffcb05] text-[11px] tracking-wider">
            POKÉREALMS
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Wallet / guest indicator */}
          {isGuest ? (
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-white/[0.04] text-white/70 text-xs">
              <User size={14} />
              <span className="mono-pixel" style={{ fontSize: 13 }}>GUEST</span>
            </div>
          ) : isConnected ? (
            <div
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#ab9ff2]/40 bg-[#ab9ff2]/10 text-[#ab9ff2]"
              title={address}
            >
              <Wallet size={14} />
              <span className="font-mono text-xs">{shortAddress}</span>
            </div>
          ) : null}

          <button
            onClick={handleReload}
            title="Reload"
            className="w-9 h-9 rounded-md border border-white/10 hover:border-[#ffcb05]/50 hover:bg-white/5 flex items-center justify-center text-white/70 hover:text-[#ffcb05] transition-colors"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={handleFullscreen}
            title="Fullscreen"
            className="w-9 h-9 rounded-md border border-white/10 hover:border-[#ffcb05]/50 hover:bg-white/5 flex items-center justify-center text-white/70 hover:text-[#ffcb05] transition-colors"
          >
            <Maximize2 size={16} />
          </button>
        </div>
      </div>

      {/* Contract Address bar — on top of the game frame */}
      <div className="h-12 px-4 sm:px-6 flex items-center justify-center bg-[#070f1c] border-b border-white/10" data-testid="play-contract-bar">
        <ContractAddress />
      </div>

      {/* Iframe area */}
      <div className="flex-1 relative bg-black">
        {!loaded && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 bg-[#0b1626]">
            <div className="pokeball float-y" style={{ width: 120, height: 120 }} />
            <div className="text-center">
              <p className="pixel-font text-[#ffcb05] text-sm mb-2">LOADING…</p>
              <p className="text-white/50 text-sm flex items-center gap-2 justify-center">
                <Volume2 size={14} /> The game may take a few seconds to load
              </p>
            </div>
          </div>
        )}

        <iframe
          key={reloadKey}
          ref={iframeRef}
          src={GAME_URL}
          title="PokéRealms"
          onLoad={() => setLoaded(true)}
          allow="autoplay; fullscreen; gamepad; microphone; accelerometer; gyroscope; clipboard-read; clipboard-write"
          allowFullScreen
          className="w-full h-full border-0"
          style={{ minHeight: "calc(100vh - 104px)" }}
        />

        {/* Tight overlay: covers only the upstream's social icons + labels in the top-right of the iframe.
            Sized just big enough to hide the two icons and their @handles. Acts as a Follow-on-X button. */}
        {loaded && (
          <a
            href="https://x.com/pokerealmsfun"
            target="_blank"
            rel="noreferrer"
            title="Follow @pokerealmsfun on X"
            className="absolute flex items-center justify-center gap-1.5 transition-colors hover:brightness-110"
            style={{
              top: 0,
              right: 0,
              width: 270,
              height: 115,
              background: "#0e3260",
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="#ffcb05" aria-hidden="true">
              <path d="M18.244 2H21l-6.49 7.41L22 22h-6.797l-4.74-6.197L4.8 22H2l6.945-7.931L2 2h6.953l4.286 5.66L18.244 2Zm-1.193 18h1.876L7.04 4H5.046l12.005 16Z" />
            </svg>
            <span className="font-mono text-white text-xs">@pokerealmsfun</span>
          </a>
        )}
      </div>
    </div>
  );
};

export default Play;
